import { faClose, faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { TemplateWrapper } from './Wrapper';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { useRef } from 'react';
import { faMicrophone } from '@fortawesome/pro-solid-svg-icons';
import _ from 'lodash';
import { InputNumber } from '@design-system/components/input/inputNumber';
import {
  Media,
  RunningExerciseSet,
  RunningExerciseType,
  runningTempoOptions,
} from '../../models/workout.domain';
import { bgColorsVars } from '@design-system/vars.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  EditWorkoutVM,
  MenuItems,
} from '../../view-models/workoutEdit.viewModel';
import { Running as RunningType } from '../../models/workout.domain';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { IconButton } from '@design-system/components/button/iconButton';
import { clsx } from 'clsx';

type RunningVariant =
  | RunningType<RunningExerciseType.Treadmill>
  | RunningType<RunningExerciseType.Outdoor>;

type RunningProps = {
  index: number;
  header: {
    icon: IconProp;
    title: string;
  };
  menuItems: MenuItems;
  expand: boolean;
} & RunningVariant &
  Pick<
    EditWorkoutVM,
    'influencerId' | 'dispatch' | 'control' | 'submitting' | 'readonly'
  >;

export const Running = (p: RunningProps) => {
  return (
    <div>
      <TemplateWrapper
        name={p.name}
        menuItems={p.menuItems}
        header={p.header}
        expand={p.expand}
      >
        <div className="bg-white w-full flex p-4 gap-6 rounded-b-xl flex-col">
          <ToggleButtonGroup
            value={p.variant}
            exclusive
            onChange={(ev, value) => {
              console.log();
              p.dispatch('changeRunningVariant', {
                index: p.index,
                variant: value,
              });
            }}
            data-testid="runningTypeToggle"
            disabled={p.readonly}
          >
            <ToggleButton
              value={RunningExerciseType.Outdoor}
              sx={{
                borderColor: 'black',
                color: 'black',
                height: '40px',
                borderRadius: '10px',
                fontWeight: 500,
                fontFamily: 'Visuelt',
                '&.Mui-selected': {
                  background: 'black',
                  color: 'white',
                  fontWeight: 500,
                },
                '&.Mui-selected:hover': {
                  background: 'black',
                  color: 'white',
                  fontWeight: 500,
                },
              }}
            >
              Outdoor
            </ToggleButton>
            <ToggleButton
              value={RunningExerciseType.Treadmill}
              sx={{
                color: 'black',
                height: '40px',
                borderColor: 'black',
                borderRadius: '10px',
                fontWeight: 500,
                fontFamily: 'Visuelt',
                '&.Mui-selected': {
                  background: 'black',
                  color: 'white',
                  fontWeight: 500,
                },
                '&.Mui-selected:hover': {
                  background: 'black',
                  color: 'white',
                  fontWeight: 500,
                },
              }}
            >
              Treadmill
            </ToggleButton>
          </ToggleButtonGroup>
          {p.variant === 'Outdoor' ? (
            <RunningSetsOutdoor
              sets={p.sets as OutdoorSets}
              exerciseIndex={p.index}
              control={p.control}
              dispatch={p.dispatch}
              submitting={p.submitting}
              readonly={p.readonly}
            />
          ) : (
            <RunningSetsTreadmill
              sets={p.sets as TreadmillSets}
              exerciseIndex={p.index}
              control={p.control}
              dispatch={p.dispatch}
              submitting={p.submitting}
              readonly={p.readonly}
            />
          )}
          <Audio
            title="Run complete"
            audio={p.media}
            loading={!!p.uploading}
            addAudio={(file: File) =>
              p.dispatch('addRunCompleteAudio', {
                index: p.index,
                uuid: p.uuid || '',
                file,
              })
            }
            removeAudio={() => p.dispatch('removeRunCompleteAudio', p.index)}
            disabled={p.readonly}
          />
        </div>
      </TemplateWrapper>
    </div>
  );
};

type TreadmillSets = RunningExerciseSet[RunningExerciseType.Treadmill][];
type RunningSetsTreadmillProps = {
  exerciseIndex: number;
  sets: TreadmillSets;
} & Pick<EditWorkoutVM, 'dispatch' | 'control' | 'submitting' | 'readonly'>;

const RunningSetsTreadmill = ({
  sets,
  exerciseIndex,
  control,
  dispatch,
  submitting,
  readonly,
}: RunningSetsTreadmillProps) => {
  return (
    <div className="flex flex-col gap-4">
      {sets.map(({ uuid, uploading }, i: number) => (
        <Controller
          key={i}
          name={`exercises.${exerciseIndex}.sets.${i}`}
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <div className="flex gap-2 items-center">
              <div className="flex items-start md:items-end gap-2 flex-col md:flex-row">
                <Typography className="w-[55px] h-[40px] flex items-center">
                  Set {i + 1}
                </Typography>
                <InputNumber
                  label="Duration"
                  disabled={submitting || readonly}
                  value={value.time}
                  onChange={(newValue) =>
                    onChange({ ...value, time: newValue })
                  }
                  error={fieldState.error?.message}
                  endCta={<Typography>sec</Typography>}
                  wrapperClassName="w-[110px]"
                  max={120}
                  min={0}
                />
                <InputNumber
                  label="Speed"
                  disabled={submitting || readonly}
                  value={value.speed}
                  onChange={(newValue) =>
                    onChange({ ...value, speed: newValue })
                  }
                  error={fieldState.error?.message}
                  endCta={<Typography>mph</Typography>}
                  wrapperClassName="w-[110px]"
                  allowDecimal={true}
                  max={12}
                  min={0}
                />
                <InputNumber
                  label="Incline"
                  disabled={submitting || readonly}
                  value={value.incline}
                  onChange={(newValue) =>
                    onChange({ ...value, incline: newValue })
                  }
                  error={fieldState.error?.message}
                  endCta={<Typography>%</Typography>}
                  wrapperClassName="w-[110px]"
                  allowDecimal={true}
                  max={14}
                  min={0}
                />
                <div className="mt-6">
                  <Audio
                    title="Add voice"
                    audio={value.audio}
                    loading={!!uploading}
                    addAudio={(file: File) =>
                      dispatch('addSetAudio', {
                        exerciseIndex: exerciseIndex,
                        setIndex: i,
                        uuid: uuid || '',
                        file,
                      })
                    }
                    removeAudio={() =>
                      dispatch('removeSetAudio', {
                        exerciseIndex: exerciseIndex,
                        setIndex: i,
                      })
                    }
                    disabled={readonly}
                  />
                </div>
                <IconButton
                  variant="text"
                  color="neutral"
                  icon={faClose}
                  onClick={() =>
                    dispatch('removeSet', { exercise: exerciseIndex, set: i })
                  }
                  disabled={readonly}
                />
              </div>
            </div>
          )}
        />
      ))}
      <Button
        color="primaryTonal"
        leftIcon={faPlus}
        className="w-[83px]"
        onClick={() => dispatch('addTreadmillSet', exerciseIndex)}
        disabled={readonly}
      >
        Set
      </Button>
    </div>
  );
};

type OutdoorSets = RunningExerciseSet[RunningExerciseType.Outdoor][];
type RunningSetsOutdoorProps = {
  exerciseIndex: number;
  sets: OutdoorSets;
} & Pick<EditWorkoutVM, 'dispatch' | 'control' | 'submitting' | 'readonly'>;
const RunningSetsOutdoor = ({
  sets,
  exerciseIndex,
  control,
  dispatch,
  submitting,
  readonly,
}: RunningSetsOutdoorProps) => {
  return (
    <div className="flex flex-col gap-4">
      {sets.map(({ uuid, uploading }, i: number) => (
        <Controller
          key={i}
          name={`exercises.${exerciseIndex}.sets.${i}`}
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <div className="flex gap-2 items-center">
              <div className="flex items-start md:items-end gap-2 flex-col md:flex-row">
                <Typography className="w-[55px] h-[40px] flex items-center">
                  Set {i + 1}
                </Typography>
                <InputNumber
                  label="Duration"
                  disabled={submitting || readonly}
                  value={value.time}
                  onChange={(newValue) =>
                    onChange({ ...value, time: newValue })
                  }
                  error={fieldState.error?.message}
                  endCta={<Typography>sec</Typography>}
                  wrapperClassName="w-[110px]"
                />
                <InputNumber
                  label="Distance"
                  disabled={submitting || readonly}
                  value={value.distance}
                  onChange={(newValue) =>
                    onChange({ ...value, distance: newValue })
                  }
                  error={fieldState.error?.message}
                  endCta={<Typography>mil</Typography>}
                  wrapperClassName="w-[110px]"
                  allowDecimal={true}
                />
                <div className="flex flex-col justify-center">
                  <Typography className="h-[28px] flex items-center">
                    Speed
                  </Typography>
                  <Select
                    value={value.tempo}
                    defaultValue={'min'}
                    sx={{
                      minWidth: '130px',
                      height: 44,
                      padding: '8px 16px',
                      border: 'solid 2px',
                      borderRadius: '10px',
                      borderColor: bgColorsVars['N-40'],
                      backgroundColor: bgColorsVars['W-100'],
                      boxShadow: 'none',
                      fontWeight: 500,
                      fontFamily: 'Visuelt',
                      fontSize: '16px',
                      margin: '1px',
                      '&.Mui-focused': {
                        margnin: 0,
                        boxShadow: 'none',
                        borderWidth: '3px',
                      },
                    }}
                    disabled={readonly}
                  >
                    {runningTempoOptions.map((option) => (
                      <MenuItem
                        key={option.label}
                        value={option.value}
                        onClick={() =>
                          onChange({ ...value, tempo: option.value })
                        }
                      >
                        <Typography variant="body" size="large">
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="mt-6">
                  <Audio
                    title="Add voice"
                    audio={value.audio}
                    loading={!!uploading}
                    addAudio={(file: File) =>
                      dispatch('addSetAudio', {
                        exerciseIndex: exerciseIndex,
                        setIndex: i,
                        uuid: uuid || '',
                        file,
                      })
                    }
                    removeAudio={() =>
                      dispatch('removeSetAudio', {
                        exerciseIndex: exerciseIndex,
                        setIndex: i,
                      })
                    }
                    disabled={readonly}
                  />
                </div>
                <IconButton
                  color="neutral"
                  variant="text"
                  icon={faClose}
                  onClick={() =>
                    dispatch('removeSet', { exercise: exerciseIndex, set: i })
                  }
                  disabled={readonly}
                />
              </div>
            </div>
          )}
        />
      ))}
      <Button
        color="primaryTonal"
        leftIcon={faPlus}
        className="w-[83px]"
        onClick={() => dispatch('addOutdoorSet', exerciseIndex)}
        disabled={readonly}
      >
        Set
      </Button>
    </div>
  );
};

const Audio = ({
  title,
  addAudio,
  audio,
  removeAudio,
  loading,
  disabled,
}: {
  title: string;
  addAudio: (file: File) => void;
  audio?: Media;
  removeAudio: () => void;
  loading: boolean;
  disabled?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const addMedia = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && !_.isEmpty(ev.target.files)) {
      addAudio(ev.target.files[0]);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center w-[165px]">
        <CircularProgress className="w-[20px] h-[20px]" />
      </div>
    );
  }
  if (!audio) {
    return (
      <>
        <Button
          onClick={() => inputRef.current?.click()}
          leftIcon={faMicrophone}
          variant="text"
          className="w-[165px]"
          disabled={disabled}
        >
          {title}
        </Button>
        <input
          type="file"
          accept="audio/x-m4a"
          ref={inputRef}
          onChange={addMedia}
          hidden
        />
      </>
    );
  }
  return (
    <div className="flex items-center bg-[#F4F4F4] rounded-[25px] max-w-[330px]">
      <audio controls src={`${audio.cdn01}${audio.audio}`}></audio>
      <FontAwesomeIcon
        icon={faClose}
        onClick={removeAudio}
        className={clsx(
          'pr-4 cursor-pointer',
          disabled && 'pointer-events-none',
        )}
      />
    </div>
  );
};
